/**
 * Reveal schedule for a scene composition (TT-1150 notes 2–4): the board's
 * model brought to the scene. Also the continuity rule between two solves
 * (TT-1162 note 1, `movedSince`): what stays, stays; what moves, glides.
 * Every element the solver emits carries the sentence it came from and the
 * block it belongs to; the schedule turns "new since the last composition"
 * into a delay per element, so a poll that adds a block paces it in rather
 * than dumping it, and nothing already on the page is touched.
 *
 * Order: block by block, blocks by their earliest sentence; within a block
 * its container/icon at the block's instant and its text lines stepped a
 * beat apart — a box never stands empty waiting for a pen. A block is what
 * the solver drew as one thing (`group`: an idea with its banner, claim and
 * quote; a theme with its headline, boxes and fragments; the decisions box
 * with its items; every solver emits through `blockSink`). The whole new
 * batch fits inside `budget`, so a first planned version
 * replacing the opening sketch (~40 elements) lands in a few seconds, not a
 * minute of one-line-at-a-time.
 *
 * Why blocks and not sentences (TT-1174 note 4): a theme's headline carries
 * the theme's first sentence while its fragments carry their own, and a
 * decisions box carries its first decision's; grouped by sentence the box
 * drew at one group and its lines trickled in over the next several —
 * every container on the page, then the text to fill them. Several blocks
 * sharing a sentence also stepped their lines as one run, so the last
 * block's box stood finished and empty for most of a second.
 *
 * Pure: `seen` is the caller's map of id → delay from earlier schedules;
 * elements it holds keep their delay (their intro already ran), elements it
 * lacks get one, and ids no longer in the composition are dropped from it
 * (a scrub backward lets them play again on the way forward).
 */

export const REVEAL = {
  groupStep: 900, // ms between sentence groups
  textLead: 350, // a group's first line starts this far into its container's draw-on
  lineStep: 220, // ms between a group's text lines
  budget: 12000, // the longest a new batch may take, whatever its size
  glide: 1100, // ms an element that kept its node but moved takes to slide to its new spot
};

function elements(comp) {
  const out = [];
  for (const t of comp?.texts || []) out.push({ id: t.id, sent: t.sent, group: t.group, text: true });
  for (const s of comp?.shapes || []) out.push({ id: s.id, sent: s.sent, group: s.group, text: false });
  for (const ic of comp?.icons || []) out.push({ id: ic.id, sent: ic.sent, group: ic.group, text: false });
  return out;
}

// Where an element sits: a text by its first line's start, a shape by its
// path's first point, an icon or image by its box. Enough to tell a move.
// Computed once per composition (`comp.origins`) so the next solve's diff
// reads it back instead of walking the old composition again.
const PATH_START = /^M\s*(-?[\d.]+)[\s,]+(-?[\d.]+)/;
export function origins(comp) {
  if (comp.origins) return comp.origins;
  const at = new Map();
  for (const t of comp.texts || []) if (t.starts?.[0]) at.set(t.id, [t.starts[0].x, t.starts[0].y]);
  for (const s of comp.shapes || []) {
    const m = PATH_START.exec(s.d || '');
    if (m) at.set(s.id, [Number(m[1]), Number(m[2])]);
    else if (s.rect) at.set(s.id, [s.rect.x, s.rect.y]); // a painted piece (TT-1330) sits by its text rect
  }
  for (const ic of comp.icons || []) at.set(ic.id, [ic.x, ic.y]);
  for (const im of comp.images || []) at.set(im.id, [im.x, im.y]);
  comp.origins = at;
  return at;
}

/**
 * The elements a new composition keeps but places elsewhere (TT-1162 note
 * 1): id → [dx, dy] from the old spot to the new. The canvas glides each
 * one across (a FLIP: rendered at its new place, started at the old), so
 * a block that a later solve nudges aside slides rather than reappears.
 * Pure. Sub-unit moves are noise, not motion.
 */
export function movedSince(prevComp, comp) {
  const moves = new Map();
  if (!prevComp || prevComp === comp) return moves;
  const before = origins(prevComp);
  for (const [id, [x, y]] of origins(comp)) {
    const was = before.get(id);
    if (!was) continue;
    const dx = x - was[0];
    const dy = y - was[1];
    if (Math.abs(dx) >= 1 || Math.abs(dy) >= 1) moves.set(id, [dx, dy]);
  }
  return moves;
}

/**
 * @param comp  a solved composition (texts / shapes / icons with `sent`)
 * @param seen  Map<id, delayMs> — mutated: pruned to the composition, new ids added
 * @returns Map<id, delayMs> (the same map)
 */
export function scheduleReveal(comp, seen) {
  const { groupStep, textLead, lineStep, budget } = REVEAL;
  const els = elements(comp);
  const ids = new Set(els.map((e) => e.id));
  for (const id of [...seen.keys()]) if (!ids.has(id)) seen.delete(id);
  const fresh = els.filter((e) => !seen.has(e.id));
  if (!fresh.length) return seen;
  // Title and anything without a sentence lead.
  const at = (e) => (Number.isFinite(e.sent) ? e.sent : -1);
  // A block's elements together, under its earliest fresh sentence.
  const groups = new Map();
  for (const e of fresh) {
    const a = at(e);
    if (!groups.has(e.group)) groups.set(e.group, { at: a, els: [] });
    const g = groups.get(e.group);
    g.at = Math.min(g.at, a);
    g.els.push(e);
  }
  const order = [...groups.values()].sort((a, b) => a.at - b.at);
  const step = Math.min(groupStep, budget / Math.max(1, order.length));
  order.forEach((g, gi) => {
    const base = Math.round(gi * step);
    let li = 0;
    for (const e of g.els) {
      // shapes and icons at the block's instant; its lines a beat apart, in order
      seen.set(e.id, e.text ? base + textLead + Math.min(lineStep * li++, step) : base);
    }
  });
  return seen;
}
